import { Box, Flex, Group, Text, Tooltip } from '@mantine/core';
import { IconComponents } from '@tabler/icons';
import ReactTimeAgo from 'react-time-ago';
import { SourceIcon } from '../../../utils/SourceIcon';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import type { EnrichedEvidence } from './types';
import { OverflownText } from '../../OverflownText';
import { formatDate } from '../../../utils';
import { resolveEvidenceDisplayOrigin } from '../../../utils/evidence/evidenceDisplayOrigin';
import { useIdeaFilters } from '../../../hooks/useIdeaFilters';
import { getOrgExtraInfoFieldsFromFiltersConfig, ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT } from '../../../utils/org-filter-config';
import { CompanyFilterExtraInfoBadges } from '../../common/CompanyFilterExtraInfoBadges';

const SOURCE_ICON_SIZE = 20;

export function DiscoveryEvidenceHeader({ evidence }: { evidence: EnrichedEvidence }) {
  const item = evidence as Record<string, unknown>;
  const businessName = item?.businessName as string | undefined;
  const company = item?.company as { name?: string } | undefined;
  const customerName =
    typeof company?.name === 'string' ? company.name.trim() : '';
  const createdAt = item?.createdAt as string | Date | undefined;
  const cxItemRec = item?.cxItem as Record<string, unknown> | undefined;
  const opportunityRec = item?.opportunity as Record<string, unknown> | undefined;
  const origin = resolveEvidenceDisplayOrigin(item as any) as string | undefined;

  const { ideasFiltersConfig } = useIdeaFilters();
  const orgExtraInfoFields = getOrgExtraInfoFieldsFromFiltersConfig(
    ideasFiltersConfig?.evidence,
    ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT
  );

  const title =
    (item?.title as string) ||
    (cxItemRec?.subject as string | undefined) ||
    (opportunityRec?.title as string | undefined) ||
    'Evidence';

  return (
    <Flex align="center" gap="sm" sx={{ minWidth: 0, width: '100%' }}>
      {origin ? (
        <Tooltip label={capitalizeFirstLetter(String(origin)) || 'Source'}>
          <Box
            sx={{
              width: SOURCE_ICON_SIZE,
              height: SOURCE_ICON_SIZE,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            mx="xs"
          >
            <SourceIcon
              width={SOURCE_ICON_SIZE}
              height={SOURCE_ICON_SIZE}
              sourceName={String(origin).toLowerCase()}
            />
          </Box>
        </Tooltip>
      ) : null}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Group position="apart" spacing={8}>
          <OverflownText fz={14} fw={500} lineClamp={2} sx={{ lineHeight: 1.35 }}>
            {title}
          </OverflownText>

          {createdAt ? (
            <Tooltip label={formatDate(String(createdAt))}>
              <Text fz={10} c="dimmed" sx={{ flexShrink: 0 }}>
                <ReactTimeAgo date={new Date(createdAt)} />
              </Text>
            </Tooltip>
          ) : null}
        </Group>
        {businessName ? (
          <Group spacing={8} align="center" mt={4} noWrap sx={{ minWidth: 0 }}>
            <Tooltip label={businessName.toUpperCase()}>
              <Text
                component="span"
                px={4}
                color="#5C5CEB"
                fz={10}
                fw={600}
                bg="rgba(92, 92, 235, 0.2)"
                lineClamp={1}
                sx={{ borderRadius: 2, flexShrink: 0 }}
              >
                {businessName.toUpperCase()}
              </Text>
            </Tooltip>
            {customerName ? (
              <Tooltip label="Customer">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    maxWidth: '100%',
                    minWidth: 0,
                    flexShrink: 1,
                    padding: '1px 6px 1px 4px',
                    lineHeight: 1,
                    backgroundColor: '#F8F9FB',
                    border: '1px solid #E9ECEF',
                    borderRadius: 4,
                    verticalAlign: 'middle',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 0,
                    }}
                  >
                    <IconComponents size={12} stroke={1.25} color="#212529" />
                  </Box>
                  <Text
                    component="span"
                    fz={11}
                    fw={500}
                    c="#212529"
                    lineClamp={1}
                    sx={{
                      minWidth: 0,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {customerName}
                  </Text>
                </Box>
              </Tooltip>
            ) : null}
          </Group>
        ) : null}
        {company ? (
          <CompanyFilterExtraInfoBadges org={company} extraInfoFields={orgExtraInfoFields} />
        ) : null}
      </Box>
    </Flex>
  );
}
